import { aplicarPorcentaje } from '../dinero';
import { diferenciaDias } from '../fechas';

export interface LineaPromocionable {
  id: string;
  tipo: 'servicio' | 'producto';
  servicioId: string | null;
  productoId: string | null;
  /** Total de la línea en centavos (precio × cantidad). */
  total: number;
  cantidad: number;
}

export interface ReglaPromocion {
  tipo: 'porcentaje' | 'monto' | 'paquete';
  /** % ×100, centavos por unidad, o precio total del paquete. */
  valor: number;
  /** Vacío en ambos = promoción de producto (cualquier producto, ningún servicio). */
  alcance: { servicioIds: string[]; productoIds: string[] };
  serviciosPaquete?: string[];
}

export interface DescuentoLinea {
  lineaId: string;
  descuento: number;
}

/**
 * ¿La promoción puede descontar esta línea? Sin servicios ni productos marcados es una promoción
 * de producto: descuenta cualquier producto y nunca servicios. Los servicios solo entran si están marcados.
 */
export function lineaEnAlcance(l: Pick<LineaPromocionable, 'tipo' | 'servicioId' | 'productoId'>, alcance: ReglaPromocion['alcance']): boolean {
  if (l.tipo === 'servicio') return alcance.servicioIds.includes(l.servicioId ?? '');
  if (alcance.servicioIds.length === 0 && alcance.productoIds.length === 0) return true;
  return alcance.productoIds.includes(l.productoId ?? '');
}

/** Descuento por línea que produce una promoción. Nunca descuenta más que el total de la línea. */
export function aplicarPromocion(lineas: LineaPromocionable[], promo: ReglaPromocion): DescuentoLinea[] {
  if (promo.tipo === 'paquete') {
    const requeridos = promo.serviciosPaquete ?? [];
    if (requeridos.length === 0) return [];
    const elegidas: LineaPromocionable[] = [];
    for (const servicioId of requeridos) {
      const l = lineas.find((x) => x.tipo === 'servicio' && x.servicioId === servicioId && !elegidas.includes(x));
      if (!l) return [];
      elegidas.push(l);
    }
    // Una unidad de cada servicio del paquete.
    const unitarios = elegidas.map((l) => Math.round(l.total / l.cantidad));
    const suma = unitarios.reduce((s, x) => s + x, 0);
    const descuento = suma - promo.valor;
    if (descuento <= 0) return [];
    let asignado = 0;
    return elegidas.map((l, i) => {
      const parte = i === elegidas.length - 1 ? descuento - asignado : Math.round((descuento * unitarios[i]!) / suma);
      asignado += parte;
      return { lineaId: l.id, descuento: parte };
    });
  }

  return lineas
    .filter((l) => lineaEnAlcance(l, promo.alcance))
    .map((l) => ({
      lineaId: l.id,
      descuento: Math.min(l.total, promo.tipo === 'porcentaje' ? aplicarPorcentaje(l.total, promo.valor / 100) : promo.valor * l.cantidad),
    }))
    .filter((d) => d.descuento > 0);
}

/**
 * ¿`hoy` cae dentro de ±`ventanaDias` del cumpleaños? Considera el cumpleaños del año anterior,
 * actual y siguiente (para cruzar fin de año). El 29 de febrero se celebra el 28 en años no bisiestos.
 */
export function esCumpleanosVigente(fechaNacimiento: string, hoy: string, ventanaDias: number): boolean {
  const [, mes, dia] = fechaNacimiento.split('-').map(Number) as [number, number, number];
  const anio = Number(hoy.slice(0, 4));
  return [anio - 1, anio, anio + 1].some((a) => {
    const bisiesto = (a % 4 === 0 && a % 100 !== 0) || a % 400 === 0;
    const d = mes === 2 && dia === 29 && !bisiesto ? 28 : dia;
    const cumple = `${a}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return Math.abs(diferenciaDias(cumple, hoy)) <= ventanaDias;
  });
}
